import React, { useEffect } from "react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useIsMobile } from "@/hooks/use-mobile";
import { Fish, AlertCircle } from "lucide-react";
interface PermitTypeSelectionProps {
  permitType: string;
  setPermitType: (type: string) => void;
  days: number;
  editData?: any; // Add editData prop for handling edit mode
}
export const PermitTypeSelection: React.FC<PermitTypeSelectionProps> = ({
  permitType,
  setPermitType,
  days,
  editData
}) => {
  const isMobile = useIsMobile();
  const isAutoSwitched = days >= 6 && permitType.startsWith("annual");

  // Set the permit type from edit data when available
  useEffect(() => {
    if (editData?.permitName) {
      // Map permit name to the correct permit type value
      const permitNameToType: Record<string, string> = {
        "All Fisheries (Ages 14+)": "daily-all",
        "Mtn. View/Sheep Creek with 2 poles": "daily-two-poles",
        "Individual Fishing - All Fisheries": "annual-individual",
        "Individual Fishing w/2 poles": "annual-individual-two-poles",
        "Family Fishing - All Fisheries": "annual-family",
        "Family Fishing w/2 poles": "annual-family-two-poles"
      };
      const derivedType = permitNameToType[editData.permitName];
      if (derivedType && derivedType !== permitType) {
        setPermitType(derivedType);
      }
    }
  }, [editData, setPermitType]);
  return <div className="mb-8">
      <h3 className="font-semibold text-lg text-tribal-secondary mb-4 flex items-center">
        <Fish className="mr-2 text-tribal-primary" size={24} /> 
        Permit Type
      </h3>
      
      {isAutoSwitched && <div className="mb-4 p-3 bg-tribal-primary/10 border border-tribal-primary/20 rounded-md flex items-start">
          <AlertCircle className="text-tribal-primary h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
          <p className="text-sm">
            We've automatically selected an annual permit as it's more cost-effective for your {days}-day trip.
            You can still select a daily permit if preferred.
          </p>
        </div>}
      
      <RadioGroup value={permitType} onValueChange={setPermitType} className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-2'}`}>
        <div className="space-y-4">
          <h4 className="font-medium text-tribal-secondary">Daily Permits</h4>
          
          <div className={`flex items-center space-x-2 rounded-md border p-3 hover:bg-tribal-light ${days >= 6 ? 'opacity-70' : ''}`}>
            <RadioGroupItem value="daily-all" id="daily-all" />
            <Label htmlFor="daily-all" className="flex-1 cursor-pointer">
              <div className="flex flex-col w-full">
                <span>All Fisheries (Ages 14+)</span>
                <span className="font-semibold py-[6px]">${(25 * days).toFixed(2)}</span>
              </div>
            </Label>
          </div>
          
          <div className={`flex items-center space-x-2 rounded-md border p-3 hover:bg-tribal-light ${days >= 6 ? 'opacity-70' : ''}`}>
            <RadioGroupItem value="daily-two-poles" id="daily-two-poles" />
            <Label htmlFor="daily-two-poles" className="flex-1 cursor-pointer">
              <div className="flex flex-col w-full">
                <span>Mtn. View/Sheep Creek with 2 poles</span>
                <span className="font-semibold py-[7px]">${(40 * days).toFixed(2)}</span>
              </div>
            </Label>
          </div>
        </div>
        
        <div className="space-y-4">
          <h4 className="font-medium text-tribal-secondary">Annual Permits (Jan 1-Dec 31)</h4>
          
          <div className={`flex items-center space-x-2 rounded-md border p-3 hover:bg-tribal-light ${permitType === "annual-individual" && isAutoSwitched ? 'bg-tribal-primary/5 border-tribal-primary/30' : ''}`}>
            <RadioGroupItem value="annual-individual" id="annual-individual" />
            <Label htmlFor="annual-individual" className="flex-1 cursor-pointer">
              <div className="flex flex-col w-full">
                <span>Individual Fishing - All Fisheries</span>
                <span className="font-semibold px-0 py-[7px]">$150.00</span>
              </div>
            </Label>
          </div>
          
          <div className={`flex items-center space-x-2 rounded-md border p-3 hover:bg-tribal-light ${permitType === "annual-individual-two-poles" && isAutoSwitched ? 'bg-tribal-primary/5 border-tribal-primary/30' : ''}`}>
            <RadioGroupItem value="annual-individual-two-poles" id="annual-individual-two-poles" />
            <Label htmlFor="annual-individual-two-poles" className="flex-1 cursor-pointer">
              <div className="flex flex-col w-full">
                <span>Individual Fishing w/2 poles</span>
                <span className="font-semibold py-[7px]">$180.00</span>
              </div>
            </Label>
          </div>
          
          <div className="flex items-center space-x-2 rounded-md border p-3 hover:bg-tribal-light">
            <RadioGroupItem value="annual-family" id="annual-family" />
            <Label htmlFor="annual-family" className="flex-1 cursor-pointer">
              <div className="flex flex-col w-full">
                <span>Family Fishing - All Fisheries</span>
                <span className="font-semibold py-[7px]">$200.00</span>
              </div>
            </Label>
          </div>
          
          <div className="flex items-center space-x-2 rounded-md border p-3 hover:bg-tribal-light">
            <RadioGroupItem value="annual-family-two-poles" id="annual-family-two-poles" />
            <Label htmlFor="annual-family-two-poles" className="flex-1 cursor-pointer">
              <div className="flex flex-col w-full">
                <span>Family Fishing w/2 poles</span>
                <span className="font-semibold py-[7px]">$220.00</span>
              </div>
            </Label>
          </div>
        </div>
      </RadioGroup>
    </div>;
};